import React, { useState, useEffect } from 'react';
import { useNotes } from '../hooks/useNotes';

const NoteModal = ({ isOpen, onClose, surahNumber, ayahNumber, surahName, ayahText }) => {
  const { getNote, saveNote, deleteNote } = useNotes();
  const [content, setContent] = useState('');
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const existingNote = getNote(surahNumber, ayahNumber);
      setContent(existingNote?.content || '');
      setIsConfirmingDelete(false);
    }
  }, [isOpen, surahNumber, ayahNumber, getNote]);

  const handleSave = () => {
    if (content.trim()) {
      saveNote({
        surahNumber,
        ayahNumber,
        surahName,
        ayahText: ayahText?.substring(0, 100) || '',
        content: content.trim(),
      });
      onClose();
    }
  };

  const handleDelete = () => {
    if (isConfirmingDelete) {
      deleteNote(surahNumber, ayahNumber);
      onClose();
    } else {
      setIsConfirmingDelete(true);
    }
  };

  const existingNote = getNote(surahNumber, ayahNumber);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[80]"
        style={{
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div 
        className="fixed left-1/2 top-1/2 z-[81] w-full max-w-2xl p-8 rounded-2xl animate-fade-in"
        style={{
          transform: 'translate(-50%, -50%)',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-gold)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 
                className="text-2xl font-bold mb-2"
                style={{ color: 'var(--text-arabic)' }}
              >
                {existingNote ? 'Edit Reflection' : 'Add Reflection'}
              </h2>
              <p 
                className="text-sm"
                style={{ color: 'var(--text-secondary)', opacity: 0.7 }}
              >
                {surahName} • Ayah {ayahNumber}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
              style={{
                color: 'var(--text-secondary)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(148, 163, 184, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              ✕
            </button>
          </div>

          {/* Ayah Context */}
          {ayahText && (
            <div 
              className="p-4 rounded-lg text-sm"
              style={{
                background: 'rgba(148, 163, 184, 0.06)',
                color: 'var(--text-secondary)',
                fontStyle: 'italic',
              }}
            >
              {ayahText.length > 150 ? `${ayahText.substring(0, 150)}...` : ayahText}
            </div>
          )}
        </div>

        {/* Text Area */}
        <div className="mb-6">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your reflection here..."
            className="w-full h-48 px-4 py-3 rounded-xl resize-none focus:outline-none transition-all text-base"
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-default)',
              color: 'var(--text-english)',
              lineHeight: 1.7,
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--gold-primary)';
              e.currentTarget.style.boxShadow = '0 0 20px var(--gold-glow)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-default)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            autoFocus
          />
          <div 
            className="flex justify-end mt-2 text-xs"
            style={{ color: 'var(--text-secondary)', opacity: 0.5 }}
          >
            {content.length} characters
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          {existingNote && (
            <button
              onClick={handleDelete}
              className="px-6 py-3 rounded-lg font-medium transition-all"
              style={{
                background: isConfirmingDelete ? 'var(--error)' : 'rgba(239, 68, 68, 0.1)',
                color: isConfirmingDelete ? 'white' : 'var(--error)',
              }}
              onMouseEnter={(e) => {
                if (!isConfirmingDelete) {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isConfirmingDelete) {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                }
              }}
            >
              {isConfirmingDelete ? 'Confirm Delete' : 'Delete'}
            </button>
          )}
          
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 rounded-lg font-medium transition-all"
            style={{
              background: 'rgba(148, 163, 184, 0.08)',
              border: '1px solid var(--border-strong)',
              color: 'var(--text-secondary)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(148, 163, 184, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)';
            }}
          >
            Cancel
          </button>
          
          <button
            onClick={handleSave}
            disabled={!content.trim()}
            className="flex-1 px-6 py-3 rounded-lg font-medium transition-all"
            style={{
              background: content.trim() 
                ? 'linear-gradient(to right, var(--gold-primary), var(--gold-hover))'
                : 'rgba(148, 163, 184, 0.3)',
              color: content.trim() ? 'white' : 'var(--text-tertiary)',
              cursor: content.trim() ? 'pointer' : 'not-allowed',
            }}
            onMouseEnter={(e) => {
              if (content.trim()) {
                e.currentTarget.style.transform = 'scale(1.02)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {existingNote ? 'Update' : 'Save'} Reflection
          </button>
        </div>
      </div>
    </>
  );
};

export default NoteModal;

